import { useRef } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 5;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const videoDuration = (url) =>
  new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => resolve(v.duration);
    v.onerror = () => reject(new Error("unreadable"));
    v.src = url;
  });

// Validates an image or ≤5s MP4 and hands back { file, url } for preview.
export const MediaPicker = ({ value, onChange, testid = "hero" }) => {
  const input = useRef(null);

  const pick = async (f) => {
    if (!f) return;
    const isVideo = f.type === "video/mp4";
    if (!isVideo && !IMAGE_TYPES.includes(f.type)) return toast.error("Use a JPG, PNG or WebP image, or an MP4 video.");
    if (f.size > (isVideo ? 25 : 10) * 1024 * 1024) return toast.error(`File too large (max ${isVideo ? 25 : 10} MB).`);
    const url = URL.createObjectURL(f);
    if (isVideo) {
      try {
        const d = await videoDuration(url);
        if (d > MAX_SECONDS + 0.25) {
          URL.revokeObjectURL(url);
          return toast.error(`This video is ${d.toFixed(1)}s — it must be 5 seconds or shorter.`);
        }
      } catch {
        toast("Couldn't preview this video here — the 5-second limit will be checked on upload.");
      }
    }
    if (value?.url) URL.revokeObjectURL(value.url);
    onChange({ file: f, url });
  };

  return (
    <>
      <input ref={input} data-testid={`${testid}-file-input`} type="file" accept="image/jpeg,image/png,image/webp,video/mp4" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {value ? (
        <div data-testid={`${testid}-preview`} className="relative aspect-video bg-[#0F0F0F] overflow-hidden">
          {value.file.type.startsWith("video") ? <video src={value.url} autoPlay muted loop playsInline className="h-full w-full object-cover" /> : <img src={value.url} alt="" className="h-full w-full object-cover" />}
          <button type="button" data-testid={`${testid}-change-file`} onClick={() => input.current?.click()} className="absolute bottom-3 right-3 bg-white/90 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase">Change</button>
        </div>
      ) : (
        <button type="button" data-testid={`${testid}-pick-button`} onClick={() => input.current?.click()} className="w-full aspect-[21/9] border border-dashed border-[#0F0F0F]/30 hover:border-[#C5A059] bg-white flex flex-col items-center justify-center gap-2 transition-colors">
          <UploadCloud className="h-7 w-7 text-[#C5A059]" strokeWidth={1.5} />
          <span className="text-sm">Choose an image or a short video</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">JPG · PNG · WebP · MP4 ≤ 5s</span>
        </button>
      )}
    </>
  );
};

export const HeroTextFields = ({ value, onChange, testid = "hero" }) => (
  <div className="grid gap-3">
    {[
      ["headline", "Headline", "New Arrivals — Beauty & Fashion", 120],
      ["subtext", "Sub-text", "Fresh pieces and beauty finds from Lamka's sellers, added daily.", 240],
      ["button_text", "Button text", "Explore the edit", 40],
    ].map(([k, label, ph, max]) => (
      <label key={k}>
        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</span>
        <input data-testid={`${testid}-text-${k}`} value={value[k] || ""} maxLength={max} placeholder={ph} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className="w-full bg-white border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-3 py-2 text-sm" />
      </label>
    ))}
    <p className="text-[11px] text-[#71717A]">Leave a field empty to use the default. Use “ — ” in the headline to make the second part gold.</p>
  </div>
);
